import { FlexCol } from '@/components/ui/flex';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import {
  RegulationsSourceList,
  StandardsSourceList,
  NewsSourceList,
} from '@/shared/subscriptions';
import {
  useSourceSubscriptionsService,
  useUpdateSourceSubscriptionAssigneesService,
} from '@/services/regulatoryWatch';
import { Loader2 } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';

type SourceTab = 'regulations' | 'standards' | 'news';

export const SubscriptionsTab: FC = () => {
  const { sourcesWithSubscriptions, bulkUpdateSubscriptions, isLoading } =
    useSourceSubscriptionsService();
  const { updateSourceAssignees } =
    useUpdateSourceSubscriptionAssigneesService();

  const handleAssignSource = useCallback(
    (sourceId: string, memberIds: string[]) => {
      updateSourceAssignees({ sourceId, memberIds });
    },
    [updateSourceAssignees]
  );

  const [activeTab, setActiveTab] = useState<SourceTab>('regulations');
  const [optimisticSourceStates, setOptimisticSourceStates] = useState<
    Record<string, boolean>
  >({});

  // Sources for the regulations grouped view
  const regulationSources = useMemo(() => {
    return sourcesWithSubscriptions
      .filter(s =>
        s.referenceTypes.some(
          t => t !== 'unknown' && (t === 'regulation' || t === 'guidance')
        )
      )
      .map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
        referenceTypes: s.referenceTypes,
        geography: s.geography,
        institutionGeographies: s.institutionGeographies,
        institutionName: s.institutionName,
        icon: s.icon,
        assignedMemberIds: s.assignedMemberIds,
      }));
  }, [sourcesWithSubscriptions]);

  // Sources for the news card view
  const newsSources = useMemo(() => {
    return sourcesWithSubscriptions
      .filter(s => s.referenceTypes.includes('news'))
      .map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
        referenceTypes: s.referenceTypes,
        geography: s.geography,
        institutionGeographies: s.institutionGeographies,
        institutionName: s.institutionName,
        icon: s.icon,
        assignedMemberIds: s.assignedMemberIds,
      }));
  }, [sourcesWithSubscriptions]);

  // Set of subscribed source IDs
  const subscribedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const source of sourcesWithSubscriptions) {
      const isSubscribed =
        source.id in optimisticSourceStates
          ? optimisticSourceStates[source.id]
          : source.subscriptionStatus === 'subscribed';
      if (isSubscribed) ids.add(source.id);
    }
    return ids;
  }, [sourcesWithSubscriptions, optimisticSourceStates]);

  const getSourceSubscriptionState = useCallback(
    (sourceId: string): boolean => {
      if (sourceId in optimisticSourceStates) {
        return optimisticSourceStates[sourceId];
      }
      const source = sourcesWithSubscriptions.find(s => s.id === sourceId);
      return source?.subscriptionStatus === 'subscribed';
    },
    [optimisticSourceStates, sourcesWithSubscriptions]
  );

  const handleToggleSource = useCallback(
    (sourceId: string) => {
      const isCurrentlySubscribed = getSourceSubscriptionState(sourceId);
      const newState = !isCurrentlySubscribed;

      setOptimisticSourceStates(prev => ({
        ...prev,
        [sourceId]: newState,
      }));

      bulkUpdateSubscriptions(
        {
          subscriptions: [
            {
              sourceId,
              status: newState ? 'subscribed' : 'unsubscribed',
            },
          ],
        },
        {
          onSettled: () => {
            setOptimisticSourceStates(prev => {
              const next = { ...prev };
              delete next[sourceId];
              return next;
            });
          },
        }
      );
    },
    [getSourceSubscriptionState, bulkUpdateSubscriptions]
  );

  const handleBulkToggle = useCallback(
    (sourceIds: string[], subscribe: boolean) => {
      const optimistic: Record<string, boolean> = {};
      const subscriptions: Array<{
        sourceId: string;
        status: 'subscribed' | 'unsubscribed';
      }> = [];

      for (const sourceId of sourceIds) {
        optimistic[sourceId] = subscribe;
        subscriptions.push({
          sourceId,
          status: subscribe ? 'subscribed' : 'unsubscribed',
        });
      }

      setOptimisticSourceStates(prev => ({ ...prev, ...optimistic }));

      bulkUpdateSubscriptions(
        { subscriptions },
        {
          onSettled: () => {
            setOptimisticSourceStates(prev => {
              const next = { ...prev };
              for (const id of sourceIds) delete next[id];
              return next;
            });
          },
        }
      );
    },
    [bulkUpdateSubscriptions]
  );

  if (isLoading) {
    return (
      <FlexCol alignItems="center" justifyContent="center" className="py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </FlexCol>
    );
  }

  return (
    <FlexCol gap="5" className="flex-1 min-h-0">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Subscriptions</h2>
        <Text variant="body-sm" color="secondary" className="mt-1">
          Manage your regulatory monitoring subscriptions.
        </Text>
      </div>

      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as SourceTab)}>
        <TabsList>
          <TabsTrigger value="regulations">Regulations & Guidances</TabsTrigger>
          <TabsTrigger value="standards">Standards</TabsTrigger>
          <TabsTrigger value="news">News</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'regulations' && (
        <div className="flex-1 min-h-0 flex flex-col max-w-[850px] border border-neutral-200 rounded-lg overflow-hidden">
          <RegulationsSourceList
            sources={regulationSources}
            selectedIds={subscribedIds}
            onToggle={handleToggleSource}
            onBulkToggle={handleBulkToggle}
            onAssignSource={handleAssignSource}
          />
        </div>
      )}

      {activeTab === 'standards' && (
        <div className="flex-1 min-h-0 flex flex-col max-w-[850px] border border-neutral-200 rounded-lg overflow-hidden">
          <StandardsSourceList
            selectedIds={subscribedIds}
            onToggle={handleToggleSource}
            onAssignSource={handleAssignSource}
          />
        </div>
      )}

      {activeTab === 'news' && (
        <div className="flex-1 min-h-0 flex flex-col max-w-[850px] border border-neutral-200 rounded-lg overflow-hidden">
          <NewsSourceList
            sources={newsSources}
            selectedIds={subscribedIds}
            onToggle={handleToggleSource}
            onBulkToggle={handleBulkToggle}
            onAssignSource={handleAssignSource}
          />
        </div>
      )}
    </FlexCol>
  );
};
